import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useLocation } from 'js/lib/useRouter';

import CampaignModulesDataContext from 'bundles/campaigns/context/CampaignModulesDataContext';
import GetCampaignModulesForLohpQuery from 'bundles/campaigns/queries/GetCampaignModulesForLohpQuery.graphql';
import type {
  GetCampaignModulesForLohpQuery as GetCampaignModulesForLohpQueryType,
  GetCampaignModulesForLohpQueryVariables,
} from 'bundles/campaigns/queries/__generated__/GetCampaignModulesForLohpQuery';
import { SentryErrorType, captureExceptionInSentry } from 'bundles/campaigns/utils/sentryUtil';
import shouldShowCampaigns from 'bundles/campaigns/utils/shouldShowCampaigns';

const CampaignModulesDataProviderForLohp = ({ children }: React.PropsWithChildren) => {
  const { query } = useLocation();
  const isPreview = query.preview === 'true' || query.preview === '1';
  const campaignId = query.campaignId ?? null;

  const { data, loading } = useQuery<GetCampaignModulesForLohpQueryType, GetCampaignModulesForLohpQueryVariables>(
    GetCampaignModulesForLohpQuery,
    {
      variables: {
        campaignModulesVariables: {
          useContentfulPreview: isPreview,
          forceUseCampaignId: campaignId,
        },
      },
      context: {
        clientName: 'gatewayGql',
      },
      errorPolicy: 'all',
      ssr: true,
      skip: !shouldShowCampaigns(),
      onError: (error) => {
        captureExceptionInSentry({
          error,
          extra: {
            message: 'Error fetching campaign modules on LOHP',
            campaignId,
            isPreview,
          },
          errorType: SentryErrorType.GRAPHQL_ERROR,
        });
      },
    }
  );

  let heroModuleData;
  let collectionsModuleData;
  let promoBarModuleData;

  const modules = data?.Campaign?.getCampaignModulesForLohp;
  if (modules?.hero?.__typename === 'Campaign_HeroModule') {
    heroModuleData = modules.hero.contentDataJson;
  }
  if (modules?.spotlightCollections?.__typename === 'Campaign_SpotlightCollectionsModule') {
    collectionsModuleData = modules.spotlightCollections.contentDataJson;
  }
  if (modules?.promoBar?.__typename === 'Campaign_PromoBarModule') {
    promoBarModuleData = modules.promoBar.contentDataJson;
  }

  return (
    <CampaignModulesDataContext.Provider
      value={{
        loading,
        campaignData: {
          heroModule: {
            contentData: heroModuleData,
          },
          collectionsModule: {
            contentData: collectionsModuleData,
          },
          promoBarModule: {
            contentData: promoBarModuleData,
          },
        },
      }}
    >
      {children}
    </CampaignModulesDataContext.Provider>
  );
};

export default CampaignModulesDataProviderForLohp;
